import { styled } from '@mui/system';
import { FC, ReactNode } from 'react';
import Container from '../Container';
import { Breakpoint, SxProps, useMediaQuery, useTheme } from '@mui/material';
import Stack from '@o2/ui/Stack';
import Box, { BoxProps } from '@o2/ui/Box';
import Divider from '@o2/ui/Divider';
import Typography from '@o2/ui/Typography';
import Link from './Link';

export interface FooterProps extends Omit<BoxProps, 'sx'> {
  /**
   * Footer links.
   *
   * Example:
   * ```<Footer.Link href="#">Odkaz</Footer.Link>```
   */
  children?: ReactNode;

  /**
   * Additional ReactNode(s) inserted into copyright section.
   */
  additionalCopyright?: ReactNode;

  /**
   * From what breakpoint to render children (links) horizontally.
   */
  childrenHorizontalFrom?: Breakpoint;

  /**
   * From what breakpoint to render copyright items (O2 + `additionalCopyright`) horizontally.
   */
  copyrightHorizontalFrom?: Breakpoint;

  /**
   * If true, rendering of copyright section is skipped.
   */
  disableCopyright?: boolean;

  /**
   * Switch between blue and default color
   */
  color?: 'default' | 'blue';
  sx?: SxProps;
}

type Footer = FC<FooterProps> & {
  Link: typeof Link;
};

const Background = styled(Box, {
  name: 'FooterBackground',
  slot: 'root',
  shouldForwardProp: (propName) => propName !== 'color',
})<{ color: FooterProps['color'] }>(({ theme, color }) => ({
  backgroundColor: color === 'blue' ? theme.palette.primary.main : undefined,
  color: color === 'blue' ? theme.palette.common.white : theme.palette.text.secondary,
  '& a': {
    color: color === 'blue' ? theme.palette.common.white : theme.palette.text.secondary,
  },
}));

/**
 * Template: 11.1.5, 11.1.5.1
 *
 * El. 11.1.5_001-007, 11.1.5.1_001-005 have to be provided by the application
 */
const Footer: Footer = ({
  children,
  additionalCopyright,
  childrenHorizontalFrom = 'md',
  copyrightHorizontalFrom = 'sm',
  disableCopyright = false,
  color = 'default',
  sx,
  ...props
}) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down(childrenHorizontalFrom));

  return (
    <Background color={color} sx={sx} {...props}>
      <Container variant="full-wide">
        <Stack
          mb={{ xs: 3, [childrenHorizontalFrom]: 0 }}
          direction={{ xs: 'column', [childrenHorizontalFrom]: 'row' }}
          justifyContent="space-between"
          columnGap={4}
          rowGap={3}
          alignItems={{ xs: 'center', [childrenHorizontalFrom]: 'start' }}
        >
          <Stack
            direction={{ xs: 'column', [childrenHorizontalFrom]: 'row' }}
            columnGap={4}
            rowGap={3}
            alignItems={{ xs: 'center', [childrenHorizontalFrom]: 'start' }}
            flexWrap="wrap"
          >
            {children}
          </Stack>
          {smallScreen && children && (
            <Box alignSelf="stretch">
              <Divider />
            </Box>
          )}
          {!disableCopyright && (
            <Stack
              direction={{ xs: 'column', [copyrightHorizontalFrom]: 'row' }}
              gap={3}
              alignItems={{ xs: 'center', [childrenHorizontalFrom]: 'start' }}
            >
              <Typography data-elm="11.1.5_008, 11.1.5.1_006" whiteSpace="nowrap">
                &copy; O2 Czech Republic a.s.
              </Typography>
              {additionalCopyright}
            </Stack>
          )}
        </Stack>
      </Container>
    </Background>
  );
};

Footer.Link = Link;

export default Footer;
